import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "../../utils/axiosAuth";
import { User, Users } from "src/@types/users";
import { RootState } from "../store";
import useAuthToken from "../useAuthToken";

interface InitialState {
  users?: Array<User>;
  total?: number;
  user: User | undefined;
  isLoading: boolean;
  msg: string | undefined;
}

export const getUsers: any = createAsyncThunk<
  Users,
  { page: number; perPage: number }
>("users/getUsers", async ({ page = 1, perPage = 10 }, { getState }) => {
  const token = (getState() as RootState).auth.accessToken;
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const response = await axios.get(
    `/user?page=${page}&perPage=${perPage}`,
    config
  );
  return response.data;
});

export const getUser: any = createAsyncThunk<User, { id: string }>(
  "users/getUser",
  async ({ id }, { getState }) => {
    const token = (getState() as RootState).auth.accessToken;
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const response = await axios.get(`/user/${id}`);
    return response.data;
  }
);

export const postNewUser: any = createAsyncThunk<
  {
    message: string;
    id: string;
  },
  { name: string; email: string; phone: string }
>("users/registerUser", async ({ name, email, phone }, { getState }) => {
  const token = (getState() as RootState).auth.accessToken;
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const response = await axios.post(
    "/user/register",
    {
      name,
      email,
      phone,
    },
    config
  );
  return response.data;
});

export const putUser: any = createAsyncThunk<
  {
    message: string;
    id: string;
  },
  {
    id: string;
    name: string;
    email: string;
    birthday: Date;
    phone: string;
    picture: string;
    userType: string;
    recoveryHash: string;
    invitationHash: string;
  }
>(
  "users/updateUser",
  async (
    {
      id,
      name,
      email,
      birthday,
      phone,
      picture,
      userType,
      recoveryHash,
      invitationHash,
    },
    { getState }
  ) => {
    const token = (getState() as RootState).auth.accessToken;
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const response = await axios.put(`/user/${id}`, {
      name,
      email,
      birthday,
      phone,
      picture,
      userType,
      recoveryHash,
      invitationHash,
    }, config);
    return response.data;
  }
);

export const blockUser: any = createAsyncThunk<
  {
    message: string;
    id: string;
  },
  {
    id: string;
    item: any;
  }
>("users/blockUser", async ({ id, item }, { getState }) => {
  const token = (getState() as RootState).auth.accessToken;
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const response = await axios.put(`/user/${id}`, { ...item }, config);
  return response.data;
});

export const deleteUser: any = createAsyncThunk<
  {
    message: string;
    id: string;
  },
  {
    id: string;
  }
>("users/deleteUser", async ({ id }, { getState }) => {
  const token = (getState() as RootState).auth.accessToken;
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const response = await axios.delete(`/user/${id}`, config);
  return response.data;
});

const initialState: InitialState = {
  users: [],
  total: 0,
  user: undefined,
  isLoading: false,
  msg: "",
};

const usersSlice = createSlice({
  name: "auth",
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getUsers.pending, (state) => {
        state.isLoading = true;
        state.msg = undefined;
      })
      .addCase(getUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.users = action.payload.data;
        state.total = action.payload.total;
        state.msg = undefined;
      })
      .addCase(getUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.msg = "Error ao fazer requisição";
      })
      .addCase(postNewUser.pending, (state) => {
        state.isLoading = true;
        state.msg = "";
      })
      .addCase(postNewUser.fulfilled, (state) => {
        state.isLoading = false;
        state.msg = "Usuário registrado!";
      })
      .addCase(postNewUser.rejected, (state) => {
        state.isLoading = false;
        state.msg = "Erro ao registrar usuário!";
      })
      .addCase(putUser.pending, (state) => {
        state.isLoading = true;
        state.msg = "";
      })
      .addCase(putUser.fulfilled, (state) => {
        state.isLoading = false;
        state.msg = "Usuário atualizado!";
      })
      .addCase(putUser.rejected, (state) => {
        state.isLoading = false;
        state.msg = "Erro ao atualizar usuário!";
      });
    // .addCase(postNewPassword.pending, (state) => {
    //   state.isLoading = true;
    //   state.messageRecovery = "";
    // })
    // .addCase(postNewPassword.fulfilled, (state) => {
    //   state.isLoading = false;
    //   state.messageRecovery = "senha alterada";
    // })
    // .addCase(postNewPassword.rejected, (state) => {
    //   state.isLoading = false;
    //   state.messageRecovery = "Erro ao alterar senha";
    // })
    // .addCase(postRefreshToken.pending, (state) => {
    //   state.isLoading = true;
    //   state.messageRecovery = "";
    // })
    // .addCase(postRefreshToken.fulfilled, (state, action) => {
    //   state.isLoading = false;
    //   state.accessToken = action.payload.accessToken;
    // })
    // .addCase(postRefreshToken.rejected, (state) => {
    //   state.accessToken = "";
    //   state.refreshToken = "";
    //   state.userReturn = initialState.userReturn;
    //   state.isLoading = false;
    //   state.error = "";
    //   state.messageRecovery = "";
    // });
  },
});

// export const {  } = usersSlice.actions;

export default usersSlice.reducer;
